import React, { useContext } from "react";
import { AuthContext } from "../authentication/Auth";

// Define all possible roles
export const ROLES = {
  ZEBRA_SUPER_USER: "zebraSuperUser",
  SUPER_ADMIN: "superAdmin",
  ADMIN: "admin",
  MANAGER: "manager",
  COMPANY_USER: "companyUser",
};

// Define all resources (pages/components)
export const RESOURCES = {
  // User Management
  USER_MANAGEMENT: "USER_MANAGEMENT",
  // Kiosk Management
  KIOSK_MANAGEMENT: "KIOSK_MANAGEMENT",
  // Site Management
  SITE_MANAGEMENT: "SITE_MANAGEMENT",
  // Kiosk Device Management
  KIOSK_DEVICE: "KIOSK_DEVICE",
  // Device Report Management
  DEVICE_REPORT: "DEVICE_REPORT",
  // Define all resources
  DASHBOARD: "DASHBOARD",
  COMPANY: "COMPANY",
  EMAIL_CONFIGURATION: "EMAIL_CONFIGURATION",
  REPORTS: "REPORTS",
};

// Define all permissions
export const PERMISSIONS = {
  // User Management permissions
  CREATE_USER: "CREATE_USER",
  READ_USER: "READ_USER",
  UPDATE_USER: "UPDATE_USER",
  DELETE_USER: "DELETE_USER",
  IMPORT_ALL_USERS: "IMPORT_ALL_USERS",
  EXPORT_ALL_USERS: "EXPORT_ALL_USERS",
  IMPORT_COMPANY_ADMIN_USERS: "IMPORT_COMPANY_ADMIN_USERS",
  EXPORT_COMPANY_ADMIN_USERS: "EXPORT_COMPANY_ADMIN_USERS",
  IMPORT_MANAGER_USERS: "IMPORT_MANAGER_USERS",
  EXPORT_MANAGER_USERS: "EXPORT_MANAGER_USERS",
  IMPORT_COMPANY_USERS: "IMPORT_COMPANY_USERS",
  EXPORT_COMPANY_USERS: "EXPORT_COMPANY_USERS",
  IMPORT_DEVICE_USERS: "IMPORT_DEVICE_USERS",
  EXPORT_DEVICE_USERS: "EXPORT_DEVICE_USERS",
  IMPORT_DEVICE_INTERNAL_USERS: "IMPORT_DEVICE_INTERNAL_USERS",
  EXPORT_DEVICE_INTERNAL_USERS: "EXPORT_DEVICE_INTERNAL_USERS",

  // Kiosk Management permissions
  CREATE_KIOSK: "CREATE_KIOSK",
  READ_KIOSK: "READ_KIOSK",
  UPDATE_KIOSK: "UPDATE_KIOSK",
  DELETE_KIOSK: "DELETE_KIOSK",

  // Site Management permissions
  CREATE_SITE: "CREATE_SITE",
  READ_SITE: "READ_SITE",
  UPDATE_SITE: "UPDATE_SITE",
  DELETE_SITE: "DELETE_SITE",
  CREATE_EMAIL_CONFIGURATION: "CREATE_EMAIL_CONFIGURATION",
  READ_EMAIL_CONFIGURATION: "READ_EMAIL_CONFIGURATION",
  UPDATE_EMAIL_CONFIGURATION: "UPDATE_EMAIL_CONFIGURATION",
  DELETE_EMAIL_CONFIGURATION: "DELETE_EMAIL_CONFIGURATION",

  // Kiosk Device Management permissions
  CREATE_KIOSK_DEVICE: "CREATE_KIOSK_DEVICE",
  READ_KIOSK_DEVICE: "READ_KIOSK_DEVICE",
  UPDATE_KIOSK_DEVICE: "UPDATE_KIOSK_DEVICE",
  DELETE_KIOSK_DEVICE: "DELETE_KIOSK_DEVICE",
  SEND_ALARM_KIOSK_DEVICE: "SEND_ALARM_KIOSK_DEVICE",
  MARKING_DEVICE_RE_COMMISSIONED_KIOSK_DEVICE: "MARKING_DEVICE_RE_COMMISSIONED_KIOSK_DEVICE",
  MARKING_DEVICE_DE_COMMISSIONED_KIOSK_DEVICE: "MARKING_DEVICE_DE_COMMISSIONED_KIOSK_DEVICE",
  MARKING_DEVICE_LOST_KIOSK_DEVICE: "MARKING_DEVICE_LOST_KIOSK_DEVICE",
  MARKING_DEVICE_FOUND_KIOSK_DEVICE: "MARKING_DEVICE_FOUND_KIOSK_DEVICE",
  UPDATE_ALIAS_KIOSK_DEVICE: "UPDATE_ALIAS_KIOSK_DEVICE",
  UPDATE_NAME_KIOSK_DEVICE: "UPDATE_NAME_KIOSK_DEVICE",

  // Device Reports Management permissions
  READ_DEVICE_REPORTS: "READ_DEVICE_REPORTS",
  EXPORT_DEVICE_REPORTS: "EXPORT_DEVICE_REPORTS",

};

// Define required permissions for each resource
const resourcePermissions = {
  [RESOURCES.USER_MANAGEMENT]: {
    view: PERMISSIONS.READ_USER,
    create: PERMISSIONS.CREATE_USER,
    update: PERMISSIONS.UPDATE_USER,
    delete: PERMISSIONS.DELETE_USER,
    importAllUsers: PERMISSIONS.IMPORT_ALL_USERS,
    exportAllUsers: PERMISSIONS.EXPORT_ALL_USERS,
    importCompanyAdminUsers: PERMISSIONS.IMPORT_COMPANY_ADMIN_USERS,
    exportCompanyAdminUsers: PERMISSIONS.EXPORT_COMPANY_ADMIN_USERS,
    importManagerUsers: PERMISSIONS.IMPORT_MANAGER_USERS,
    exportManagerUsers: PERMISSIONS.EXPORT_MANAGER_USERS,
    importCompanyUsers: PERMISSIONS.IMPORT_COMPANY_USERS,
    exportCompanyUsers: PERMISSIONS.EXPORT_COMPANY_USERS,
    importDeviceUsers: PERMISSIONS.IMPORT_DEVICE_USERS,
    exportDeviceUsers: PERMISSIONS.EXPORT_DEVICE_USERS,
    importDeviceInternalUsers: PERMISSIONS.IMPORT_DEVICE_INTERNAL_USERS,
    exportDeviceInternalUsers: PERMISSIONS.EXPORT_DEVICE_INTERNAL_USERS,
  },
  [RESOURCES.KIOSK_MANAGEMENT]: {
    view: PERMISSIONS.READ_KIOSK,
    create: PERMISSIONS.CREATE_KIOSK,
    update: PERMISSIONS.UPDATE_KIOSK,
    delete: PERMISSIONS.DELETE_KIOSK,
  },
  [RESOURCES.SITE_MANAGEMENT]: {
    view: PERMISSIONS.READ_SITE,
    create: PERMISSIONS.CREATE_SITE,
    update: PERMISSIONS.UPDATE_SITE,
    delete: PERMISSIONS.DELETE_SITE,
    createEmailConfiguration: PERMISSIONS.CREATE_EMAIL_CONFIGURATION,
    readEmailConfiguration: PERMISSIONS.READ_EMAIL_CONFIGURATION,
    updateEmailConfiguration: PERMISSIONS.UPDATE_EMAIL_CONFIGURATION,
    deleteEmailConfiguration: PERMISSIONS.DELETE_EMAIL_CONFIGURATION,
  },
  [RESOURCES.DEVICE_REPORT]: {
    view: PERMISSIONS.READ_DEVICE_REPORTS,
    exportDeviceReports: PERMISSIONS.EXPORT_DEVICE_REPORTS,

  },
  [RESOURCES.KIOSK_DEVICE]: {
    view: PERMISSIONS.READ_USER,
    create: PERMISSIONS.CREATE_USER,
    update: PERMISSIONS.UPDATE_USER,
    delete: PERMISSIONS.DELETE_USER,
    sendAlarm: PERMISSIONS.SEND_ALARM_KIOSK_DEVICE,
    markingDeviceReCommissioned: PERMISSIONS.MARKING_DEVICE_RE_COMMISSIONED_KIOSK_DEVICE,
    markingDeviceDeCommissioned: PERMISSIONS.MARKING_DEVICE_DE_COMMISSIONED_KIOSK_DEVICE,
    markingDeviceLost: PERMISSIONS.MARKING_DEVICE_LOST_KIOSK_DEVICE,
    markingDeviceFound: PERMISSIONS.MARKING_DEVICE_FOUND_KIOSK_DEVICE,
    updateAlias: PERMISSIONS.UPDATE_ALIAS_KIOSK_DEVICE,
    updateNameKioskDevice: PERMISSIONS.UPDATE_NAME_KIOSK_DEVICE,
  },
};

// Define permission mappings for each role
const rolePermissions = {
  [ROLES.ZEBRA_SUPER_USER]: [...Object.values(PERMISSIONS)],
  [ROLES.SUPER_ADMIN]: [
    ...Object.values(PERMISSIONS).filter((p) => p !== PERMISSIONS.CREATE_KIOSK),
  ], // For super user creation is through device enrollment
  [ROLES.ADMIN]: [
    // User Management
    PERMISSIONS.READ_USER,
    PERMISSIONS.CREATE_USER,
    PERMISSIONS.UPDATE_USER,
    PERMISSIONS.DELETE_USER,
    PERMISSIONS.IMPORT_COMPANY_ADMIN_USERS,
    PERMISSIONS.EXPORT_COMPANY_ADMIN_USERS,
    PERMISSIONS.IMPORT_MANAGER_USERS,
    PERMISSIONS.EXPORT_MANAGER_USERS,
    PERMISSIONS.IMPORT_COMPANY_USERS,
    PERMISSIONS.EXPORT_COMPANY_USERS,
    // Kiosk Management
    PERMISSIONS.CREATE_KIOSK,
    PERMISSIONS.READ_KIOSK,
    PERMISSIONS.UPDATE_KIOSK,
    // Site Management
    PERMISSIONS.CREATE_SITE,
    PERMISSIONS.READ_SITE,
    PERMISSIONS.UPDATE_SITE,
    PERMISSIONS.DELETE_SITE,
    PERMISSIONS.CREATE_EMAIL_CONFIGURATION,
    PERMISSIONS.READ_EMAIL_CONFIGURATION,
    PERMISSIONS.UPDATE_EMAIL_CONFIGURATION,
    PERMISSIONS.DELETE_EMAIL_CONFIGURATION,
    // Kiosk Device Management
    PERMISSIONS.READ_KIOSK_DEVICE,
    PERMISSIONS.UPDATE_ALIAS_KIOSK_DEVICE,
    PERMISSIONS.UPDATE_KIOSK_DEVICE,
    PERMISSIONS.MARKING_DEVICE_RE_COMMISSIONED_KIOSK_DEVICE,
    PERMISSIONS.MARKING_DEVICE_DE_COMMISSIONED_KIOSK_DEVICE,
    PERMISSIONS.MARKING_DEVICE_LOST_KIOSK_DEVICE,
    PERMISSIONS.MARKING_DEVICE_FOUND_KIOSK_DEVICE,
    // Device Report Management
    PERMISSIONS.READ_DEVICE_REPORTS,
    PERMISSIONS.EXPORT_DEVICE_REPORTS,

    // Add permissions here for ADMIN
  ],
  [ROLES.MANAGER]: [
    // User Management
    PERMISSIONS.READ_USER,
    // DGN-1066 : Manager cannot create or edit or update or delete other users
    // PERMISSIONS.CREATE_USER,
    // PERMISSIONS.UPDATE_USER,
    // PERMISSIONS.DELETE_USER,
    // PERMISSIONS.IMPORT_DEVICE_USERS,
    PERMISSIONS.EXPORT_DEVICE_USERS,
    // PERMISSIONS.IMPORT_DEVICE_INTERNAL_USERS,
    PERMISSIONS.EXPORT_DEVICE_INTERNAL_USERS,

    // Kiosk Management
    PERMISSIONS.CREATE_KIOSK,
    PERMISSIONS.READ_KIOSK,
    // PERMISSIONS.UPDATE_KIOSK,
    // Site Management
    // PERMISSIONS.CREATE_SITE,
    PERMISSIONS.READ_SITE,
    PERMISSIONS.UPDATE_SITE,
    // PERMISSIONS.CREATE_EMAIL_CONFIGURATION,
    PERMISSIONS.READ_EMAIL_CONFIGURATION,
    PERMISSIONS.UPDATE_EMAIL_CONFIGURATION,
    // PERMISSIONS.DELETE_EMAIL_CONFIGURATION,
    // Site Device Management
    // Kiosk Device Management
    PERMISSIONS.READ_KIOSK_DEVICE,
    PERMISSIONS.SEND_ALARM_KIOSK_DEVICE,
    PERMISSIONS.UPDATE_KIOSK_DEVICE,
    PERMISSIONS.UPDATE_ALIAS_KIOSK_DEVICE,
    PERMISSIONS.MARKING_DEVICE_RE_COMMISSIONED_KIOSK_DEVICE,
    PERMISSIONS.MARKING_DEVICE_DE_COMMISSIONED_KIOSK_DEVICE,
    PERMISSIONS.MARKING_DEVICE_LOST_KIOSK_DEVICE,
    PERMISSIONS.MARKING_DEVICE_FOUND_KIOSK_DEVICE,
    // Device Report Management
    PERMISSIONS.READ_DEVICE_REPORTS,
    PERMISSIONS.EXPORT_DEVICE_REPORTS,
    // Add permissions here for MANAGER
  ],
  [ROLES.COMPANY_USER]: [
    // User Management
    PERMISSIONS.READ_USER,
    // Kiosk Management
    PERMISSIONS.READ_KIOSK,
    // Site Management
    PERMISSIONS.READ_SITE,
    PERMISSIONS.READ_EMAIL_CONFIGURATION,
    // Kiosk Device Management
    PERMISSIONS.READ_KIOSK_DEVICE,
    // Device Report Management
    PERMISSIONS.READ_DEVICE_REPORTS,
    PERMISSIONS.EXPORT_DEVICE_REPORTS,
    // Add permissions here for COMPANY_USER
  ],
};

// Get user role from claims - with null check
export const getUserRole = (claims) => {
  if (!claims) return null;

  if (claims.isDTRKAdmin) return ROLES.ZEBRA_SUPER_USER;
  if (claims.isSuperAdmin) return ROLES.SUPER_ADMIN;
  if (claims.isAdmin) return ROLES.ADMIN;
  if (claims.isManager) return ROLES.MANAGER;
  if (claims.isCompanyUser) return ROLES.COMPANY_USER;
  return null;
};

// Check if user has specific permission - with null check
export const hasPermission = (claims, permission) => {
  if (!claims) return false;

  const role = getUserRole(claims);
  if (!role) return false;
  return rolePermissions[role].includes(permission);
};

// Check if user has access to a resource action - with null check
export const hasResourcePermission = (claims, resource, action = "view") => {
  if (!claims) return false;

  const requiredPermission = resourcePermissions[resource]?.[action];
  if (!requiredPermission) return false;
  return hasPermission(claims, requiredPermission);
};

// Get all permissions for a user - with null check
export const getUserPermissions = (claims) => {
  if (!claims) return [];

  const role = getUserRole(claims);
  if (!role) return [];
  return rolePermissions[role];
};

// Higher-order component for protecting routes/components - using context hook
export const withRBAC = (
  WrappedComponent,
  resource,
  action = "view"
) => {
  return function RBACWrapper(props) {
    const authContext = useContext(AuthContext);
    const claims = authContext?.currentUser?.claims;
    const hasAccess = hasResourcePermission(
      claims,
      resource,
      action
    );

    if (!hasAccess) {
      return null; // Or return an unauthorized component
    }

    return <WrappedComponent {...props} />;
  };
};

// Custom hook for checking permissions
export const usePermissions = () => {
  const authContext = useContext(AuthContext);
  const claims = authContext?.currentUser?.claims;

  return {
    canViewDashboard: hasResourcePermission(
      claims,
      RESOURCES.DASHBOARD,
      "view"
    ),
    canViewCompany: hasResourcePermission(
      claims,
      RESOURCES.COMPANY,
      "view"
    ),
    canViewSite: hasResourcePermission(
      claims,
      RESOURCES.SITE_MANAGEMENT,
      "view"
    ),
    canViewKiosk: hasResourcePermission(
      claims,
      RESOURCES.KIOSK_MANAGEMENT,
      "view"
    ),
    canViewCabinetDevice: hasResourcePermission(
      claims,
      RESOURCES.KIOSK_DEVICE,
      "view"
    ),

    // User Management permissions
    canCreateUser: hasPermission(claims, PERMISSIONS.CREATE_USER),
    canReadUser: hasPermission(claims, PERMISSIONS.READ_USER),
    canUpdateUser: hasPermission(claims, PERMISSIONS.UPDATE_USER),
    canDeleteUser: hasPermission(claims, PERMISSIONS.DELETE_USER),
    canImportAllUsers: hasPermission(claims, PERMISSIONS.IMPORT_ALL_USERS),
    canExportAllUsers: hasPermission(claims, PERMISSIONS.EXPORT_ALL_USERS),
    canImportCompanyAdminUsers: hasPermission(
      claims,
      PERMISSIONS.IMPORT_COMPANY_ADMIN_USERS
    ),
    canExportCompanyAdminUsers: hasPermission(
      claims,
      PERMISSIONS.EXPORT_COMPANY_ADMIN_USERS
    ),
    canImportManagerUsers: hasPermission(
      claims,
      PERMISSIONS.IMPORT_MANAGER_USERS
    ),
    canExportManagerUsers: hasPermission(
      claims,
      PERMISSIONS.EXPORT_MANAGER_USERS
    ),
    canImportCompanyUsers: hasPermission(
      claims,
      PERMISSIONS.IMPORT_COMPANY_USERS
    ),
    canExportCompanyUsers: hasPermission(
      claims,
      PERMISSIONS.EXPORT_COMPANY_USERS
    ),
    canImportDeviceUsers: hasPermission(
      claims,
      PERMISSIONS.IMPORT_DEVICE_USERS
    ),
    canExportDeviceUsers: hasPermission(
      claims,
      PERMISSIONS.EXPORT_DEVICE_USERS
    ),
    canImportDeviceInternalUsers: hasPermission(
      claims,
      PERMISSIONS.IMPORT_DEVICE_INTERNAL_USERS
    ),
    canExportDeviceInternalUsers: hasPermission(
      claims,
      PERMISSIONS.EXPORT_DEVICE_INTERNAL_USERS
    ),

    // Kiosk Management permissions
    canCreateKiosk: hasPermission(claims, PERMISSIONS.CREATE_KIOSK),
    canReadKiosk: hasPermission(claims, PERMISSIONS.READ_KIOSK),
    canUpdateKiosk: hasPermission(claims, PERMISSIONS.UPDATE_KIOSK),
    canDeleteKiosk: hasPermission(claims, PERMISSIONS.DELETE_KIOSK),

    // Site Management permissions
    canCreateSite: hasPermission(claims, PERMISSIONS.CREATE_SITE),
    canReadSite: hasPermission(claims, PERMISSIONS.READ_SITE),
    canUpdateSite: hasPermission(claims, PERMISSIONS.UPDATE_SITE),
    canDeleteSite: hasPermission(claims, PERMISSIONS.DELETE_SITE),
    canCreateEmailConfiguration: hasPermission(
      claims,
      PERMISSIONS.CREATE_EMAIL_CONFIGURATION
    ),
    canReadEmailConfiguration: hasPermission(
      claims,
      PERMISSIONS.READ_EMAIL_CONFIGURATION
    ),
    canUpdateEmailConfiguration: hasPermission(
      claims,
      PERMISSIONS.UPDATE_EMAIL_CONFIGURATION
    ),
    canDeleteEmailConfiguration: hasPermission(
      claims,
      PERMISSIONS.DELETE_EMAIL_CONFIGURATION
    ),

    // Kiosk Device Management permissions
    canCreateKioskDevice: hasPermission(
      claims,
      PERMISSIONS.CREATE_KIOSK_DEVICE
    ),
    canReadKioskDevice: hasPermission(claims, PERMISSIONS.READ_KIOSK_DEVICE),
    canUpdateKioskDevice: hasPermission(
      claims,
      PERMISSIONS.UPDATE_KIOSK_DEVICE
    ),
    canDeleteKioskDevice: hasPermission(
      claims,
      PERMISSIONS.DELETE_KIOSK_DEVICE
    ),
    canSendAlarm: hasPermission(claims, PERMISSIONS.SEND_ALARM_KIOSK_DEVICE),
    canMarkDeviceReCommissioned: hasPermission(
      claims,
      PERMISSIONS.MARKING_DEVICE_RE_COMMISSIONED_KIOSK_DEVICE
    ),
    canMarkDeviceDeCommissioned: hasPermission(
      claims,
      PERMISSIONS.MARKING_DEVICE_DE_COMMISSIONED_KIOSK_DEVICE
    ),
    canMarkDeviceLost: hasPermission(
      claims,
      PERMISSIONS.MARKING_DEVICE_LOST_KIOSK_DEVICE
    ),
    canMarkDeviceFound: hasPermission(
      claims,
      PERMISSIONS.MARKING_DEVICE_FOUND_KIOSK_DEVICE
    ),
    canUpdateAlias: hasPermission(
      claims,
      PERMISSIONS.UPDATE_ALIAS_KIOSK_DEVICE
    ),
    canUpdateNameKioskDevice: hasPermission(
      claims,
      PERMISSIONS.UPDATE_NAME_KIOSK_DEVICE
    ),

    // Device Report Management permissions
    canReadDeviceReports: hasPermission(claims, PERMISSIONS.READ_DEVICE_REPORTS),
    canExportDeviceReports: hasPermission(
      claims,
      PERMISSIONS.EXPORT_DEVICE_REPORTS
    ),
    // Functions
    hasResourcePermission: (resource, action) =>
      hasResourcePermission(claims, resource, action),

    // Other properties
    userRole: getUserRole(claims),
  };
};
